import classNames from 'classnames';
import { LogoBox } from 'components/ui/LogoBox';
import { ILogoItem, Logo } from 'components/ui/LogoBox/Logo';
import { SIZE } from 'components/ui/Tabs/constants';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';

import { FETCH_PARAMS, TABS, TYPES } from '../../constants/partners';
import { getPartnersList, makeEvenList } from '../../helpers/partnersList';
import { getBanksList } from '../../services/banks';
import { getInsurancesList } from '../../services/insurances';
import { useRatings } from '../../store/reducers/ratings';
import commonStyles from '../../styles/common.module.scss';
import Tabs from '../ui/Tabs';

import styles from './partners.module.scss';

export interface IBankItem {
  name: string;
  logo: string;
  alias: string;
  complexCalculatedRatingValue: number;
  approved: number;
  organizationType: 'bank';
}

export interface IMfoItem {
  name: string;
  logo: string;
  alias: string;
  complexCalculatedRatingValue: number;
  approved: number;
  organizationType: 'mfo';
}

export interface IInsuranceItem {
  name: string;
  id: number;
  alias: string;
  complexCalculatedRatingValue: number;
  approved: number;
  organizationType: 'insuranceCompany';
}

const generateLogoItemFromBank = (item: IBankItem): ILogoItem => ({
  name: item.name,
  imgUrl: item.logo,
  itemUrl: `/bank/${item.alias}/otzyvy/`,
  rating: item.complexCalculatedRatingValue > 5 ? 5 : item.complexCalculatedRatingValue,
  reviewsCount: item.approved,
});

const generateLogoItemFromMfo = (item: IMfoItem): ILogoItem => {
  return {
    name: item.name,
    imgUrl: item.logo,
    itemUrl: `zaimy/mfo/${item.alias}/otzyvy/`,
    rating: item.complexCalculatedRatingValue > 5 ? 5 : item.complexCalculatedRatingValue,
    reviewsCount: item.approved,
  };
};

const generateLogoItemFromInsurance = (item: IInsuranceItem): ILogoItem => {
  return {
    name: item.name,
    imgUrl: `//f.sravni.ru/logotypes/ic/sq/logo_${item.id}.svg`,
    itemUrl: `/strakhovaja-kompanija/${item.alias}/otzyvy/`,
    rating: item.complexCalculatedRatingValue > 5 ? 5 : item.complexCalculatedRatingValue,
    reviewsCount: item.approved,
  };
};

const fetchFunctionMap = {
  [TYPES.BANK]: getBanksList,
  [TYPES.INSURANCE]: getInsurancesList,
  [TYPES.MFI]: getBanksList, // it's normal
};

const Partners = React.memo(() => {
  const ratings = useRatings();
  const [partners, setPartners] = useState({});

  const tabsList = useMemo(() => {
    return Object.values(TABS).reduce((acc, tab) => {
      if (partners[tab.value]?.length) {
        return [...acc, tab.label];
      }
      return acc;
    }, []);
  }, [partners]);

  const [activeTab, setActiveTab] = useState<number>(0);
  const activeType = useMemo(() => {
    if (!tabsList.length) return null;

    return Object.values(TABS).find((tab) => tab.label === tabsList[activeTab]).value;
  }, [activeTab, tabsList]);

  const isMobile = useIsMobile();

  const fetchOrganization = useCallback(
    async (type: TYPES) => {
      const items = await fetchFunctionMap[type](FETCH_PARAMS[type]);
      let res = getPartnersList({ organizations: items, ratings: ratings[type] });

      res = makeEvenList(res);

      setPartners((prev) => ({
        ...prev,
        [type]: res,
      }));
    },
    [ratings],
  );

  useEffect(() => {
    if (Object.keys(partners).length) return;

    async function fetchData() {
      await Promise.all([
        fetchOrganization(TYPES.BANK),
        fetchOrganization(TYPES.MFI),
        fetchOrganization(TYPES.INSURANCE),
      ]);
    }

    fetchData();
  }, [fetchOrganization, partners]);

  function handleChangeActiveTab(value) {
    setActiveTab(value);
  }

  const isBank = useMemo(() => tabsList[activeType] === TYPES.BANK, [activeType, tabsList]);

  const listData = useMemo(() => {
    if (!Object.keys(partners).length || !activeType) return [];

    return partners[activeType];
  }, [activeType, partners]);

  const generateLogoItem = useCallback((item: IBankItem | IInsuranceItem | IMfoItem): ILogoItem => {
    if (item.organizationType === 'insuranceCompany') return generateLogoItemFromInsurance(item as IInsuranceItem);

    if (item.organizationType === 'bank') {
      return generateLogoItemFromBank(item as IBankItem);
    } else if (item.organizationType === 'mfo') {
      return generateLogoItemFromMfo(item as IMfoItem);
    }

    return null;
  }, []);

  const mobileContent = useMemo(
    () => (
      <div className={commonStyles.wrapper}>
        <div className={styles['mobile-logo-container']}>
          {listData?.map((item, i) => (
            <Logo item={generateLogoItem(item)} isBank={isBank} key={`partner-${i}`} />
          ))}
        </div>
      </div>
    ),
    [generateLogoItem, isBank, listData],
  );

  const desktopContent = useMemo(
    () => (
      <div className={'h-mt-32'}>
        <LogoBox items={listData?.map((item) => generateLogoItem(item))} isBank={isBank} />
      </div>
    ),
    [generateLogoItem, isBank, listData],
  );

  if (!listData?.length || !activeType) return null;

  return (
    <section className={styles.partnersBlock}>
      <div className={commonStyles.wrapper}>
        <div className={classNames(styles.inner, styles['inner--start'])}>
          <Typography.Heading level={2}>Наши партнёры</Typography.Heading>
          <Tabs active={activeTab} items={tabsList} onChange={handleChangeActiveTab} size={SIZE.SMALL} />
        </div>
      </div>
      {isMobile ? mobileContent : desktopContent}
      <div className={commonStyles.wrapper}>
        <div className={classNames(styles.inner, styles['inner--final'])}>
          <Typography.Text size={14} className={styles.commonLink}>
            <Typography.Link href={TABS[activeType].link} strong>
              {TABS[activeType].linkText}
            </Typography.Link>
          </Typography.Text>
        </div>
      </div>
    </section>
  );
});

export default Partners;
